import React, { useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Spinner,
    Badge,
    Input,
} from 'reactstrap';
import { getData, postData } from '../../../CommonElements/Functions/PostGetFun';
import { API } from '../../../api/API';
import { toast } from 'react-toastify';
import moment from 'moment';
import FeatherIconCom from '../../../Utils/CommonComponents/CommonIcons/FeatherIconCom';
import Loader from '../../../Layout/Loader';

type Props = {
    employeeIdProp?: number;
    positionId?: number;
};

export default function Holidays({ employeeIdProp, positionId }: Props) {
    const serverUrl = localStorage.getItem("serverUrl");
    const [holidays, setHolidays] = useState<any[]>([]);
    const [optionalHolidays, setOptionalHolidays] = useState<any[]>([]);
    const [submittedHolidaysMap, setSubmittedHolidaysMap] = useState<Record<string, string>>({});
    const [selectedHolidays, setSelectedHolidays] = useState<any[]>([]);

    const [loading, setLoading] = useState<boolean>(true);
    const [submitting, setSubmitting] = useState<boolean>(false);

    const getDayColor = (dayName: string) => {
        switch ((dayName || '').toLowerCase()) {
            case 'monday': return '#0D47A1';
            case 'tuesday': return '#1976D2';
            case 'wednesday': return '#00ACC1';
            case 'thursday': return '#00897B';
            case 'friday': return '#0288D1';
            case 'saturday': return '#7E57C2';
            case 'sunday': return '#E53935';
            default: return '#6c757d';
        }
    };

    const fetchHolidaysData = async () => {
        if (!serverUrl) return;
        setLoading(true);
        try {
            // 1. Fetch Company Holidays
            const holidayUrl = `${serverUrl}${API.getHolidayList}`;
            const holidayRes = await getData(holidayUrl, true);
            const holidayData = holidayRes?.data?.data || holidayRes?.data || [];
            
            let companyList: any[] = [];
            if (Array.isArray(holidayData)) {
                companyList = holidayData.filter((item: any) => item.HolidayId || item.id).map((item: any) => {
                    const fullDate = moment(item.HolidayDate || item.holiday_date, 'YYYY-MM-DD');
                    return {
                        ...item,
                        HolidayId: item.HolidayId || item.id,
                        HolidayName: item.HolidayName || item.holiday_name || item.name,
                        HolidayDateFormatted: fullDate.isValid() ? fullDate.format('D MMMM YYYY') : (item.HolidayDate || item.holiday_date),
                        Day: fullDate.isValid() ? fullDate.format('dddd') : '',
                    };
                });
            }
            setHolidays(companyList);

            // 2. Fetch Optional Holidays
            const optUrl = `${serverUrl}${API.getOptionalHolidays}`;
            const optRes = await getData(optUrl, true);
            const optData = optRes?.data?.data || optRes?.data || [];

            let optList: any[] = [];
            if (Array.isArray(optData)) {
                optList = optData.filter((item: any) => item.HolidayId || item.OptionalHolidayId || item.id).map((item: any) => {
                    const hId = item.HolidayId || item.OptionalHolidayId || item.id;
                    const fullDate = moment(item.HolidayDate || item.holiday_date, 'YYYY-MM-DD');
                    return {
                        ...item,
                        HolidayId: hId,
                        HolidayName: item.HolidayName || item.holiday_name || item.name,
                        HolidayDateFormatted: fullDate.isValid() ? fullDate.format('D MMMM YYYY') : (item.HolidayDate || item.holiday_date),
                        Day: fullDate.isValid() ? fullDate.format('dddd') : '',
                    };
                });
            }
            setOptionalHolidays(optList);

            // 3. Fetch User's Holiday Requests Status
            const reqUrl = `${serverUrl}${API.getHolidayRequests}`;
            const params: any = { module_name: 'Holiday' };
            if (positionId) params.position_id = positionId;

            const reqRes = await getData(reqUrl, true, params);
            const reqData = reqRes?.data?.data || reqRes?.data || [];
            
            const statusMap: Record<string, string> = {};
            if (Array.isArray(reqData)) {
                reqData.forEach((req: any) => {
                    const reqStatus = req.status || req.Status || req.holidays?.[0]?.Status || 'pending';
                    const list = req.holidays || req.items || [];
                    if (Array.isArray(list)) {
                        list.forEach((h: any) => {
                            const hId = h.HolidayId || h.holiday_id || h.id;
                            if (hId) {
                                statusMap[String(hId)] = h.Status || reqStatus;
                            }
                        });
                    }
                });
            }
            setSubmittedHolidaysMap(statusMap);

        } catch (error) {
            console.error("Error fetching holidays:", error);
            toast.error("Failed to load holiday data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHolidaysData();
    }, [serverUrl, positionId]);

    const toggleHolidaySelection = (holidayId: string | number) => {
        const idStr = String(holidayId);
        const existingStatus = submittedHolidaysMap[idStr];
        if (existingStatus && (existingStatus.toLowerCase() === 'pending' || existingStatus.toLowerCase() === 'approved')) {
            return;
        }

        setSelectedHolidays(prev => {
            if (prev.includes(idStr)) {
                return prev.filter(id => id !== idStr);
            }
            return [...prev, idStr];
        });
    };

    const handleSubmitOptionalHolidays = async () => {
        if (!selectedHolidays.length || !serverUrl) return;
        setSubmitting(true);
        try {
            const url = `${serverUrl}${API.submitHolidayRequest}`;
            const selectedIdsStr = selectedHolidays.join(',');
            const payload = {
                optional_holiday_ids: selectedIdsStr,
                holiday_ids: selectedIdsStr,
            };

            const response = await postData(url, true, payload);
            const ok = response?.status === 200 || response?.data?.statusCode === 200;

            if (ok) {
                toast.success(response?.data?.message || "Optional holidays submitted successfully!");
                setSelectedHolidays([]);
                fetchHolidaysData();
            } else {
                toast.error(response?.data?.message || response?.data?.error || "Failed to submit optional holidays");
            }
        } catch (error: any) {
            console.error("Submit optional holidays error:", error);
            toast.error(error?.message || "An error occurred while submitting request");
        } finally {
            setSubmitting(false);
        }
    };

    const renderStatusBadge = (status: string) => {
        const s = (status || '').toLowerCase();
        let color = 'secondary';
        if (s === 'approved') color = 'success';
        else if (s === 'pending') color = 'warning';
        else if (s === 'rejected') color = 'danger';

        return (
            <Badge color={color} className="px-2 py-1 fw-bold text-uppercase" style={{ fontSize: '0.72rem' }}>
                {status}
            </Badge>
        );
    };

    if (loading) {
        return (
            <CardBody className="p-5">
                <Loader height="250px" />
            </CardBody>
        );
    }

    return (
        <CardBody className="p-4" style={{ color: '#000000' }}>
            {/* Company Holidays Section */}
            <div className="mb-4">
                <h5 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                    <FeatherIconCom iconName="Calendar" size={20} className="text-primary" />
                    <span>Company Holidays</span>
                </h5>

                {holidays.length > 0 ? (
                    <Row className="g-3">
                        {holidays.map((item, idx) => (
                            <Col md={6} key={item.HolidayId || idx}>
                                <Card className="border shadow-none bg-light h-100 mb-0" style={{ borderRadius: '10px', borderColor: '#e0e0e0' }}>
                                    <CardBody className="p-3 text-center d-flex flex-column align-items-center justify-content-center">
                                        <div className="fw-bold text-dark mb-2" style={{ fontSize: '1rem', color: '#000000' }}>
                                            {item.HolidayDateFormatted}
                                        </div>
                                        {item.Day && (
                                            <Badge
                                                className="px-3 py-1 mb-2 fw-bold text-white"
                                                style={{ backgroundColor: getDayColor(item.Day), borderRadius: '6px', fontSize: '0.82rem' }}
                                            >
                                                {item.Day}
                                            </Badge>
                                        )}
                                        <div className="fw-bold text-secondary" style={{ fontSize: '0.95rem' }}>
                                            {item.HolidayName}
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                ) : (
                    <div className="p-3 text-muted border rounded text-center">No company holidays found</div>
                )}
            </div>

            {/* Optional Holidays Section */}
            <div className="mt-4 pt-3 border-top">
                <h5 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                    <FeatherIconCom iconName="CheckSquare" size={20} className="text-success" />
                    <span>Optional Holidays</span>
                </h5>

                {optionalHolidays.length > 0 ? (
                    <>
                        <Row className="g-3 mb-4">
                            {optionalHolidays.map((item, idx) => {
                                const hIdStr = String(item.HolidayId);
                                const isSelected = selectedHolidays.includes(hIdStr);
                                const existingStatus = submittedHolidaysMap[hIdStr];
                                const hasStatus = !!existingStatus;

                                return (
                                    <Col md={6} key={item.HolidayId || idx}>
                                        <Card
                                            className={`border shadow-none h-100 mb-0 position-relative ${hasStatus ? 'bg-light opacity-75' : isSelected ? 'border-primary bg-white' : 'bg-light'}`}
                                            style={{
                                                borderRadius: '10px',
                                                borderColor: isSelected ? '#0d6efd' : '#e0e0e0',
                                                borderWidth: isSelected ? '2px' : '1px',
                                                cursor: hasStatus ? 'not-allowed' : 'pointer'
                                            }}
                                            onClick={() => !hasStatus && toggleHolidaySelection(item.HolidayId)}
                                        >
                                            {/* Top Right Checkbox or Status Badge */}
                                            <div className="position-absolute top-0 end-0 p-2 me-1 mt-1">
                                                {hasStatus ? (
                                                    renderStatusBadge(existingStatus)
                                                ) : (
                                                    <Input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleHolidaySelection(item.HolidayId)}
                                                        style={{ cursor: 'pointer', transform: 'scale(1.15)' }}
                                                    />
                                                )}
                                            </div>

                                            <CardBody className="p-3 text-center d-flex flex-column align-items-center justify-content-center">
                                                <div className="fw-bold text-dark mb-2 mt-2" style={{ fontSize: '1rem', color: '#000000' }}>
                                                    {item.HolidayDateFormatted}
                                                </div>
                                                {item.Day && (
                                                    <Badge
                                                        className="px-3 py-1 mb-2 fw-bold text-white"
                                                        style={{ backgroundColor: getDayColor(item.Day), borderRadius: '6px', fontSize: '0.82rem' }}
                                                    >
                                                        {item.Day}
                                                    </Badge>
                                                )}
                                                <div className="fw-bold text-secondary" style={{ fontSize: '0.95rem' }}>
                                                    {item.HolidayName}
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>

                        {/* Submit Request Button */}
                        <div className="d-grid mt-3">
                            <Button
                                color="primary"
                                className="py-2.5 fw-bold text-white d-flex align-items-center justify-content-center gap-2"
                                disabled={selectedHolidays.length === 0 || submitting}
                                style={{ backgroundColor: '#0d6efd', borderRadius: '8px', fontSize: '1rem' }}
                                onClick={handleSubmitOptionalHolidays}
                            >
                                {submitting ? (
                                    <>
                                        <Spinner size="sm" color="light" />
                                        <span>Submitting Request...</span>
                                    </>
                                ) : (
                                    <>
                                        <FeatherIconCom iconName="Send" size={18} />
                                        <span>Submit Request ({selectedHolidays.length} Selected)</span>
                                    </>
                                )}
                            </Button>
                        </div>
                    </>
                ) : (
                    <div className="p-3 text-muted border rounded text-center">No optional holidays available</div>
                )}
            </div>
        </CardBody>
    );
}
